// Where we're working — one map per region, dots coloured by stage and sized by
// book value.
//
// Leaflet is the global the app already loads for the Map tab (public/index.html).
// Tiles are Esri's dark gray canvas: keyless, where CARTO's dark basemap now
// watermarks every tile without an API key.
//
// Each panel refits whenever its box changes size. Fitting once on mount lost
// the race against fonts and the scrollbar arriving, and computed the zoom for
// a box that had not finished laying out — Florida came up at continent scale.
import { useEffect, useRef } from "react";
import { stageRamp } from "../common/brand";
import { STAGE_LABEL } from "../../services/homeView";

const ESRI = "https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/";
const M = (v) => `$${(v / 1e6).toFixed(1)}M`;
const esc = (s) => String(s).replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));

function RegionMap({ region }) {
  const ref = useRef(null);

  useEffect(() => {
    const L = window.L;
    if (!L || !ref.current) return undefined;
    const map = L.map(ref.current, { zoomControl: false, scrollWheelZoom: false, attributionControl: true });
    L.tileLayer(`${ESRI}World_Dark_Gray_Base/MapServer/tile/{z}/{y}/{x}`, { attribution: "Esri", maxZoom: 16 }).addTo(map);
    L.tileLayer(`${ESRI}World_Dark_Gray_Reference/MapServer/tile/{z}/{y}/{x}`, { maxZoom: 16, opacity: 0.7 }).addTo(map);

    const pts = [];
    // Largest first, so the small ones sit on top and stay hoverable.
    [...region.rows].sort((a, b) => b.value - a.value).forEach((r) => {
      pts.push([r.lat, r.lng]);
      L.circleMarker([r.lat, r.lng], {
        radius: 5 + Math.sqrt(Math.max(r.value, 0) / 1e6) * 1.55,
        color: "#242424", weight: 2, fillColor: stageRamp[r.stage], fillOpacity: 0.92,
      })
        .bindTooltip(
          `<b>${esc(r.name)}</b><br><span style="color:#8a8a8a">${STAGE_LABEL[r.stage]} · ${esc(r.market)}</span><br>${r.value > 0 ? M(r.value) : "Unpriced"} · ${Math.round(r.sf).toLocaleString()} SF`,
          { direction: "top", offset: [0, -6], className: "home-map-tip" })
        .addTo(map);
    });

    const fit = () => {
      map.invalidateSize();
      if (pts.length === 1) map.setView(pts[0], 10, { animate: false });
      else if (pts.length) map.fitBounds(pts, { padding: [38, 38], maxZoom: 10, animate: false });
    };
    fit();
    const ro = new ResizeObserver(fit);
    ro.observe(ref.current);
    return () => { ro.disconnect(); map.remove(); };
  }, [region]);

  return <div ref={ref} className="h-[340px] rounded-[10px] border border-[#3a3a3a] bg-[#191919]" />;
}

export default function WhereMaps({ regions }) {
  const cols = regions.length > 1
    ? `minmax(0,2fr) ${regions.slice(1).map(() => "minmax(0,1fr)").join(" ")}`
    : "minmax(0,1fr)";
  return (
    <section data-print-block className="rounded-[14px] border border-[#3a3a3a] bg-[#242424] px-6 py-5">
      <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="text-[15px] font-semibold text-[#f4f4f4]">Where we're working</h3>
          <p className="mt-0.5 text-[12px] text-[#8a8a8a]">Every deal in underwriting or later, sized by value</p>
        </div>
        <div className="flex flex-wrap gap-x-4 gap-y-1">
          {Object.keys(STAGE_LABEL).map((s) => (
            <span key={s} className="flex items-center gap-1.5 text-[12px] text-[#b4b4b4]">
              <span className="inline-block h-[9px] w-[9px] rounded-full" style={{ background: stageRamp[s] }} />
              {STAGE_LABEL[s]}
            </span>
          ))}
        </div>
      </div>
      {window.L ? (
        <div className="grid gap-2.5" style={{ gridTemplateColumns: cols }}>
          {regions.map((g) => (
            <div key={g.name}>
              <div className="mb-1.5 ml-0.5 text-[10px] uppercase tracking-[0.14em] text-[#8a8a8a]">{g.name}</div>
              <RegionMap region={g} />
            </div>
          ))}
        </div>
      ) : (
        <div className="py-10 text-[12px] text-[#8a8a8a]">The map library didn't load — check the connection.</div>
      )}
    </section>
  );
}
